import type { BondOrder } from './editor';
import type { DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from './graphDrawing.ts';
import type { PaperSize } from './geometry.ts';
import {
  ATOM_ATTACH_DISTANCE_PX,
  DEFAULT_BOND_DRAWING_CONFIG,
  connectedBondAngle,
  distanceBetween,
  nearestAtomWithin,
  normalizedToPixel,
  pointAtAngle,
  type PixelPoint
} from './bonds.ts';

export interface ChainTemplatePlacement {
  readonly lines: readonly DrawingLine[];
}

// A raw (unclamped) division, matching ringTemplates.ts's own line() helper -- as opposed to
// bonds.ts's pixelToNormalized, which clamps to [0,1]. Clamping here would hide an out-of-bounds
// chain drag from usePaperObjects.ts's editingBounds rejection check by collapsing every endpoint
// onto the paper edge instead of leaving it genuinely out of bounds (#449).
function line(id: string, start: PixelPoint, end: PixelPoint, size: PaperSize): DrawingLine {
  return {
    id,
    pointerType: 'mouse',
    createdAt: new Date().toISOString(),
    start: { x: start.x / size.width, y: start.y / size.height },
    end: { x: end.x / size.width, y: end.y / size.height }
  };
}

const MINIMUM_DRAG_DISTANCE_PX = 0.5;

// Half the zigzag's per-bond deviation from the trend line: a standard chain vertex angle of
// DEFAULT_BOND_DRAWING_CONFIG.connectedBondAngleDegrees (120 degrees) means each bond turns 60
// degrees off the previous one, split evenly to either side of the straight-line trend direction.
const HALF_SPREAD_DEGREES = (180 - DEFAULT_BOND_DRAWING_CONFIG.connectedBondAngleDegrees) / 2;

/**
 * Builds a zigzag chain from a drag gesture: `start` anchors the chain (snapping to a nearby
 * atom, if any), and the vector from `start` to `current` sets the chain's length (rounded to
 * whole bonds) and its trend direction - the straight line the zigzag oscillates around is
 * exactly the angle between `start` and `current`, so the chain always points where the user
 * dragged. Each bond alternates HALF_SPREAD_DEGREES to either side of that trend line.
 *
 * When the chain starts on an existing atom, its first bond instead continues that atom's own
 * open bond angle (via connectedBondAngle against its real neighbors, the same primitive the
 * Draw tool uses) rather than the drag trend, so extending an existing structure still looks
 * like a natural continuation of it.
 */
export function createChainTemplatePlacement(
  start: NormalizedPoint,
  current: NormalizedPoint,
  bondLength: number,
  size: PaperSize,
  atoms: readonly DrawingAtom[],
  existingLines: readonly DrawingLine[],
  bondOrderByLineId?: Readonly<Record<string, BondOrder>>
): ChainTemplatePlacement {
  const startPixel = normalizedToPixel(start, size);
  const attachedAtom = nearestAtomWithin(startPixel, atoms, size, ATOM_ATTACH_DISTANCE_PX);
  const anchorPoint = attachedAtom ? attachedAtom.position : start;
  const anchorPixel = normalizedToPixel(anchorPoint, size);
  const currentPixel = normalizedToPixel(current, size);

  const dragDistance = distanceBetween(anchorPixel, currentPixel);
  const chainLength = Math.max(1, Math.round(dragDistance / bondLength));
  const trendAngle =
    dragDistance < MINIMUM_DRAG_DISTANCE_PX
      ? DEFAULT_BOND_DRAWING_CONFIG.defaultAngleDegrees
      : (Math.atan2(currentPixel.y - anchorPixel.y, currentPixel.x - anchorPixel.x) * 180) / Math.PI;

  const lines: DrawingLine[] = [];
  let currentPoint = anchorPoint;
  let allLines = existingLines;

  for (let index = 0; index < chainLength; index += 1) {
    const angle =
      index === 0 && attachedAtom
        ? connectedBondAngle(
            currentPoint,
            allLines,
            size,
            DEFAULT_BOND_DRAWING_CONFIG.connectedBondAngleDegrees,
            bondOrderByLineId
          )
        : trendAngle + (index % 2 === 0 ? -HALF_SPREAD_DEGREES : HALF_SPREAD_DEGREES);
    const startBondPixel = normalizedToPixel(currentPoint, size);
    const endPixel = pointAtAngle(startBondPixel, bondLength, angle);
    const newLine = line(
      globalThis.crypto?.randomUUID?.() ?? `chain-${Date.now()}-${index}`,
      startBondPixel,
      endPixel,
      size
    );
    lines.push(newLine);
    allLines = [...allLines, newLine];
    currentPoint = newLine.end;
  }

  return { lines };
}
